/**
 * POS Shift Z-Report — opens a print window summarising a closed shift.
 *
 * Fed the object returned by posShiftHelper.closeShift (opening/expected/counted/
 * variance + per-method totals). 80mm thermal layout, mirrors POSReceipt's
 * window.open + window.print mechanism.
 */

import { formatCurrency } from '../../utils/formatters';
import { isRawBTEnabled, printZReportViaRawBT } from '../../utils/escposReceipt';
import { isPrintBrandHidden, PRINT_BRAND_TEXT } from '../../utils/printBranding';

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const money = (v) => esc(formatCurrency(Number(v) || 0));

const dt = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-PK', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return esc(iso); }
};

export const printZReport = (shift, activeCompany, posReceiptSettings = null) => {
  if (!shift) return;

  // Bluetooth thermal path (mobile POS via RawBT) — same as the receipt.
  if (isRawBTEnabled()) {
    try { printZReportViaRawBT(shift, activeCompany, posReceiptSettings || {}); }
    catch (err) { console.warn('RawBT Z-report failed:', err); }
    return;
  }

  // Follow the POS receipt's paper size: 58mm when the receipt is 58mm,
  // otherwise the standard 80mm. (Content kept a touch narrower than the paper
  // so the right-aligned amount clears the printer's physical margin.)
  const is58 = posReceiptSettings?.paperSize === '58mm';
  const bodyWidth = is58 ? '56mm' : '80mm';
  const bodyFont = is58 ? '11px' : '13px';

  const companyName = esc(activeCompany?.companyName || activeCompany?.name || 'AMAL ERP');
  const variance = Number(shift.variance) || 0;
  const varianceColor = Math.abs(variance) < 0.01 ? '#15803D' : '#B91C1C';

  const row = (label, value, opts = {}) => `
    <div class="row ${opts.total ? 'total' : ''}" ${opts.color ? `style="color:${opts.color}"` : ''}>
      <span>${esc(label)}</span><b>${value}</b>
    </div>`;

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Z-Report ${esc(shift.shiftNumber || '')}</title>
  <style>
    * { box-sizing: border-box; }
    body { width: ${bodyWidth}; margin: 0; padding: 6px 8px; font-family: 'Courier New', monospace; font-size: ${bodyFont}; color: #000; }
    .center { text-align: center; }
    .title { font-weight: 900; font-size: 15px; margin: 6px 0 2px; }
    .muted { color: #444; font-size: 12px; }
    .hr { border-top: 1px dashed #000; margin: 8px 0; }
    .row { display: flex; justify-content: space-between; padding: 3px 0; }
    .row.total { border-top: 1px solid #000; margin-top: 4px; padding-top: 6px; font-weight: 900; font-size: 14px; }
    .sec { font-weight: 700; margin: 8px 0 2px; text-transform: uppercase; font-size: 12px; }
    @media print { body { width: ${bodyWidth}; } }
  </style>
</head>
<body>
  <div class="center">
    <div style="font-weight:900;font-size:16px;">${companyName}</div>
    <div class="title">SHIFT Z-REPORT</div>
    <div class="muted">${esc(shift.shiftNumber || '')}</div>
  </div>
  <div class="hr"></div>

  ${row('Counter', esc(`${shift.counterName || ''} (${shift.counterCode || ''})`))}
  ${shift.branchName ? row('Branch', esc(shift.branchName)) : ''}
  ${row('Opened by', esc(shift.openedByName || '-'))}
  ${row('Opened at', dt(shift.openedAt))}
  ${row('Closed by', esc(shift.closedByName || '-'))}
  ${row('Closed at', dt(shift.closedAt))}

  <div class="sec">Sales</div>
  ${row(`Sales (${shift.saleCount || 0})`, money(shift.grossSales))}
  ${row(`Returns (${shift.returnCount || 0})`, '-' + money(shift.grossReturns))}

  <div class="sec">By Payment</div>
  ${row('Cash Sales', money(shift.systemCashSales))}
  ${row('Cash Refunds', '-' + money(shift.systemCashReturns))}
  ${row('Card / Bank Sales', money(shift.systemCardSales))}
  ${row('Credit Sales', money(shift.systemCreditSales))}

  <div class="sec">Cash Drawer</div>
  ${row('Opening Cash', money(shift.openingCash))}
  ${row('Expected Cash', money(shift.expectedCash))}
  ${row('Counted Cash', money(shift.countedCash))}
  ${row('Variance', money(variance), { total: true, color: varianceColor })}

  ${shift.notes ? `<div class="hr"></div><div class="muted">Notes: ${esc(shift.notes)}</div>` : ''}

  <div class="hr"></div>
  <div class="center muted">Printed ${dt(new Date().toISOString())}</div>
  ${isPrintBrandHidden(activeCompany) ? '' : `<div class="center" style="margin-top:4px;font-family:Arial,Helvetica,sans-serif;font-size:10px;color:#666;">${PRINT_BRAND_TEXT}</div>`}

  <script>
    window.onload = function(){ try { window.print(); } catch(e) {} };
  </script>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=350,height=620');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
};

export default printZReport;
